import React, { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useLocation, useParams } from "react-router";
import { shallowEqual, useSelector } from "react-redux";
import {
  selectFavorites,
  selectLanguageOrDefault,
  selectLeagueMatches,
  selectMatchElements,
  selectMatchStatus
} from "../../../selectors";
import LeagueMatchSection from "../LeagueMatches";
import { pathHome, pathLeague } from "../../../config/routes";
import { useMatchesFetcher } from "../../../hooks/useMatchesFetcher";
import { useMatchesSections } from "../../../hooks/useMatchesSections";
import { useStableId } from "../../../hooks/useStableId";
import { AdPositionEnum } from "../../../client/api";
import { generateAds, isAdString } from "../../../config/utils";
import EmptyState from "../EmptyState";
import type { SectionItem } from "../LeagueMatches";
import { AdSlot } from "../AdSlot";

const NO_LEAGUE_ID = -1;

type MatchesCardProps = {
  selectedDate: Date;
};

const MatchesCard: React.FC<MatchesCardProps> = ({ selectedDate }) => {
  const l = useSelector(selectLanguageOrDefault);
  const cardStableId = useStableId("matches-card");
  const location = useLocation();
  const leagueMatchesResponse = useSelector(selectLeagueMatches, shallowEqual);
  const matchElements = useSelector(selectMatchElements);
  const matchStatus = useSelector(selectMatchStatus);
  const favorites = useSelector(selectFavorites);
  const params = useParams<{ leagueId?: string; date?: string }>();

  const { realLeagueId, urlDate } = useMemo(() => {
    const idStr = params.leagueId;
    const id = idStr ? parseInt(idStr, 10) : NaN;
    return {
      realLeagueId: Number.isNaN(id) ? NO_LEAGUE_ID : id,
      urlDate: params.date
    };
  }, [params.leagueId, params.date]);

  const isHomePage = useMemo(
    () => location.pathname.startsWith(pathHome()),
    [location.pathname]
  );

  const { sections, leagueMatches, matches } = useMatchesSections({
    leagueMatchesResponse,
    matchElements,
    favorites,
    selectedDate,
    realLeagueId,
    language: l
  });

  useMatchesFetcher({
    selectedDate,
    urlDate,
    realLeagueId,
    isHomePage
  });

  const sectionsWithAds = useMemo(() => {
    if (!isHomePage) {
      return sections.map((section) => ({
        ...section,
        dataWithAds: section.data as (SectionItem | string)[],
      }));
    }

    type FlatItem = { sectionIndex: number; item: SectionItem };

    const flatItems: FlatItem[] = [];

    sections.forEach((section, sectionIndex) => {
      const isFavoritesSection = Boolean(section.meta?.isFavorites);
      if (isFavoritesSection) {
        return;
      }
      section.data.forEach((item) => {
        flatItems.push({ sectionIndex, item });
      });
    });

    const itemsWithAds = generateAds(
      flatItems,
      (entry): boolean =>
        typeof entry.item !== "string" && !("dateFrom" in entry.item)
    );
    const result = sections.map((section) => ({
      ...section,
      dataWithAds: [] as (SectionItem | string)[],
    }));

    let lastSectionIndex = -1;

    itemsWithAds.forEach((entry) => {
      if (typeof entry === "string") {
        if (lastSectionIndex >= 0) {
          result[lastSectionIndex].dataWithAds.push(entry);
        }
        return;
      }

      const { sectionIndex, item } = entry as FlatItem;
      result[sectionIndex].dataWithAds.push(item);
      lastSectionIndex = sectionIndex;
    });

    sections.forEach((section, sectionIndex) => {
      const isFavoritesSection = Boolean(section.meta?.isFavorites);
      if (isFavoritesSection) {
        result[sectionIndex].dataWithAds = section.data as (SectionItem | string)[];
      }
    });

    return result;
  }, [sections, isHomePage]);

  const renderSectionTitle = (title: { name: string; id?: number }) => {
    const baseClasses =
      "text-font dark:text-font-dark font-medium text-sm text-left";

    if (typeof title.id === "number") {
      return (
        <NavLink
          to={pathLeague(title.id)}
          className={`${baseClasses} hover:text-primary dark:hover:text-primary transition-colors`}
        >
          {title.name}
        </NavLink>
      );
    }

    return <div className={baseClasses}>{title.name}</div>;
  };

  if (sections.length === 0) {
    const isLoading = matchStatus === "loading";
    const nothingOnHome = realLeagueId === NO_LEAGUE_ID && (leagueMatches?.length ?? 0) === 0;
    const nothingOnLeague = realLeagueId !== NO_LEAGUE_ID && (matches?.length ?? 0) === 0;

    if (nothingOnHome || nothingOnLeague) {
      return (
        <EmptyState
          loading={isLoading}
          loadingMessageKey="loadingMatches"
          messageKey="noMatches"
          className="w-full"
        />
      );
    }
    return null;
  }
  return (
    <>
      {sectionsWithAds.map((section) => {
        const isFavoritesSection = Boolean(section.meta?.isFavorites);
        const sectionKeySeed = section.title.id ?? section.title.name;
        const listKey = `${cardStableId}-${sectionKeySeed}`;

        return (
          <React.Fragment key={listKey}>
            <div className="mb-6">
              {section.title && (
                <div className="mb-2">
                  {renderSectionTitle(section.title)}
                </div>
              )}
              {section.dataWithAds.map((item) => {
                if (isAdString(item)) {
                  return (
                    <AdSlot
                      key={item}
                      type={AdPositionEnum.HOME_MATCH}
                      className="block w-full mb-2"
                    />
                  );
                }
                return (
                  <React.Fragment
                    key={`item-${
                      (item.id)
                    }`}
                  >
                    <LeagueMatchSection
                      section={{ title: section.title, data: [item as SectionItem] }}
                      showLeagueBanner={isFavoritesSection}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default MatchesCard;
